<template>
	<div
		class="flex max-w-full flex-row flex-nowrap gap-x-2 gap-y-1 overflow-x-auto md:flex-wrap md:overflow-visible"
		role="group"
		:tabindex="profileDeactivated ? 0 : null"
		:aria-label="profileDeactivated ? $t('application.profile-deactivated.explanation') : null"
	>
		<!-- Copy link button (full modal variant, org context only) -->
		<Button
			v-if="variant === 'full' && isOrg"
			type="secondary"
			class="shrink-0 border-[#DADCDD] text-[#171717] whitespace-nowrap"
			@click="copyLink"
		>
			<span class="inline-flex items-center gap-2 whitespace-nowrap">
				<IconLink class="shrink-0" />
				<span>
					{{ copied ? $t('general.link-copied') : $t('general.copy-link') }}
				</span>
			</span>
		</Button>

		<!-- Organization: No application - Invite -->
		<Button
			v-if="isOrg && !application && showInvite && canInvite"
			:type="variant === 'full' ? 'secondary' : 'secondary'"
			:size="variant === 'compact' ? 'sm' : undefined"
			class="shrink-0"
			:loading="isLoading"
			@click="$emit('invite')"
		>
			<Icon :icon="['far', 'paper-plane']" class="shrink-0" />
			<span v-if="variant === 'full'" class="whitespace-nowrap">
				{{ $t('general.invite') }}
			</span>
		</Button>

		<!-- Organization: Application exists -->
		<template v-else-if="isOrg && application && canRecruit">
			<!-- Invitation status: Revoke (table/kanban) or Decline (modal) -->
			<Button
				:aria-label="$t('general.revoke-invitation')"
				v-if="status === 'invitation'"
				:type="variant === 'compact' ? 'danger' : 'secondary'"
				:size="variant === 'compact' ? 'sm' : undefined"
				:class="variant === 'full' ? 'shrink-0 text-red-700' : ''"
				:loading="isLoading"
					:disabled="profileDeactivated"
				@click="update('denied')"
				v-tooltip="
					variant === 'compact'
						? $t('general.revoke-invitation')
						: null
				"
				icon="times"
			>
				<span v-if="variant === 'full'" class="whitespace-nowrap">
					{{ $t('general.revoke-invitation') }}
				</span>
			</Button>

			<!-- Other statuses: Shortlist, Interview, Hire, Decline -->
			<template v-else>
				<Button
					:aria-label="$t('general.status.shortlist')"
					v-if="
						status === 'pending' ||
							(status === 'denied' && !isArchiveLocked)
					"
					:type="variant === 'compact' ? 'success' : 'secondary'"
					:size="variant === 'compact' ? 'sm' : undefined"
					class="shrink-0"
					:loading="isLoading"
					:disabled="profileDeactivated"
					@click="update('accepted')"
					v-tooltip="
						variant === 'compact'
							? shortlistLabel
							: null
					"
					icon="check"
				>
					<span v-if="variant === 'full'" class="whitespace-nowrap">
						{{ shortlistLabel }}
					</span>
				</Button>

				<DeclineApplicationAction
					v-if="status !== 'denied'"
					:application-id="application.id"
					:applications="[application]"
					:button-type="variant === 'compact' ? 'danger-light' : 'secondary'"
					:size="variant === 'compact' ? 'sm' : null"
					:compact="variant === 'compact'"
					:button-class="variant === 'full' ? 'text-red-700' : ''"
					:loading="isLoading"
					:disabled="profileDeactivated"
					@update="decline"
				/>

				<Button

					:aria-label="$t('general.interview')"
					v-if="status === 'accepted'"
					:type="variant === 'compact' ? 'success' : 'secondary'"
					:size="variant === 'compact' ? 'sm' : undefined"
					:icon-only="variant === 'compact'"
					class="shrink-0"
					:loading="isLoading"
					:disabled="profileDeactivated"
					@click="update('interview')"
					v-tooltip="
						variant === 'compact'
							? $t('general.status.interview')
							: null
					"
				>
					<span class="inline-flex items-center gap-2">
						<IconCalendarEmpty size="16" class="shrink-0" />
						<span v-if="variant === 'full'" class="whitespace-nowrap">
							{{ $t('general.interview') }}
						</span>
					</span>
				</Button>

				<Button

					:aria-label="$t('general.hire')"
					v-if="status === 'interview'"
					:type="variant === 'compact' ? 'success' : 'secondary'"
					:size="variant === 'compact' ? 'sm' : undefined"
					class="shrink-0"
					:loading="isLoading"
					:disabled="profileDeactivated"
					@click="update('hired')"
					v-tooltip="
						variant === 'compact' ? $t('general.status.hire') : null
					"
					icon="handshake"
				>
					<span v-if="variant === 'full'" class="whitespace-nowrap">
						{{ $t('general.hire') }}
					</span>
				</Button>
			</template>
		</template>

		<!-- Applicant: Accept/Decline for invitation -->
		<template v-else-if="!isOrg && application && status === 'invitation'">
			<Button
				:type="variant === 'compact' ? 'danger' : 'danger'"
				:size="variant === 'compact' ? 'sm' : undefined"
				class="shrink-0"
				:loading="isLoading"
				@click="update('denied')"
				v-tooltip="variant === 'compact' ? $t('general.decline') : null"
				icon="times"
			>
				<span v-if="variant === 'full'" class="whitespace-nowrap">
					{{ $t('general.decline') }}
				</span>
			</Button>
			<Button
				type="success"
				:size="variant === 'compact' ? 'sm' : undefined"
				class="shrink-0"
				:loading="isLoading"
				@click="update('accepted')"
				v-tooltip="variant === 'compact' ? $t('general.accept') : null"
				icon="check"
			>
				<span v-if="variant === 'full'" class="whitespace-nowrap">
					{{ $t('crud.accept') }}
				</span>
			</Button>
		</template>

		<!-- Applicant: Contact for accepted -->
		<Button
			v-else-if="!isOrg && application && status === 'accepted'"
			type="tertiary"
			@click="$emit('contact')"
			icon="envelope"
		>
			{{ $t('general.contact') }}
		</Button>
	</div>
</template>

<script>
import { isApplicationProfileDeactivated } from '~/utils/applicationAvailability'
import Button from '~/components/ui/common/Button'
import Icon from '~/components/ui/common/Icon'
import { DeclineApplicationAction } from '~/components/Applications'
import { IconCalendarEmpty, IconLink } from '~/components/Icons'
import { isApplicationArchiveLocked } from '~/utils/applicationDeclineSource'
import { copyTextToClipboard } from '~/utils/clipboard'

export default {
	components: {
		Button,
		DeclineApplicationAction,
		Icon,
		IconCalendarEmpty,
		IconLink
	},
	props: {
		application: { type: Object, default: null },
		jobOffer: { type: Object, default: null },
		isOrg: { type: Boolean, required: true },
		loading: { type: [Boolean, Array], default: false },
		showInvite: { type: Boolean, default: false },
		variant: {
			type: String,
			default: 'full',
			validator: v => ['full', 'compact'].includes(v)
		}
	},
	data() {
		return {
			copied: false,
			copiedTimeout: null
		}
	},
	computed: {
		canInvite() {
			return this.jobOffer?.capabilities?.recruit === true
		},
		canRecruit() {
			return this.application?.capabilities?.recruit === true
		},
		profileDeactivated() {
			return this.isOrg && isApplicationProfileDeactivated(this.application)
		},
		status() {
			return this.application?.status ?? null
		},
		isLoading() {
			if (typeof this.loading === 'boolean') return this.loading
			if (Array.isArray(this.loading) && this.application?.id) {
				return this.loading.includes(this.application.id)
			}
			return false
		},
		isArchiveLocked() {
			return isApplicationArchiveLocked(this.application)
		},
		shortlistLabel() {
			const shortlist = this.$t('general.status.shortlist')
			if (shortlist !== 'general.status.shortlist') {
				return shortlist
			}

			const locale = this.$i18n?.locale || this.$store?.state?.locale || 'en'
			return locale === 'de' ? 'Vorauswahl' : 'Shortlist'
		}
	},
	beforeDestroy() {
		if (this.copiedTimeout) clearTimeout(this.copiedTimeout)
	},
	methods: {
		update(newStatus) {
			if (this.profileDeactivated || (this.isOrg && this.application?.capabilities?.recruit !== true)) return
			this.$emit('update', this.application.id, newStatus)
		},
		decline(id, status, sendRejectionEmail, rejectionResult) {
			if (!rejectionResult && this.profileDeactivated) return
			this.$emit(
				'update',
				id,
				status,
				sendRejectionEmail,
				...(rejectionResult ? [rejectionResult] : [])
			)
		},
		async copyLink() {
			const url = window.location.href
			if (await copyTextToClipboard(url)) {
				this.showCopiedFeedback()
			}
		},
		showCopiedFeedback() {
			if (this.$breakpoint('md')) {
				this.$store.dispatch('toast/push', {
					title: this.$t('toast.applicant-profile-link-copied.title'),
					message: this.$t(
						'toast.applicant-profile-link-copied.message'
					),
					type: 'success'
				})
				return
			}

			this.copied = true
			if (this.copiedTimeout) clearTimeout(this.copiedTimeout)
			this.copiedTimeout = setTimeout(() => {
				this.copied = false
			}, 2000)
		}
	}
}
</script>

<style scoped>
[role='group'] ::v-deep button {
	flex-shrink: 0;
	width: auto;
	max-width: none;
}

[role='group'] ::v-deep button:disabled {
	@apply bg-grey-50 text-grey-500 border-neutral-100 !important;
}
</style>
